import { EventType } from "@/global/store/data/types";
import { convertShortDate } from "@/global/utils/dateTimeConvert";
import Bowser from "bowser";
import {
  startOfWeek,
  eachDayOfInterval,
  getDay,
  isSameMonth,
  isSameWeek,
  format,
  min,
  max,
} from "date-fns";

export interface DateRangeProps {
  startDate: Date;
  endDate: Date;
}

// Get list of Dates to String of Dates as the backend request
export const formatDatesToStrings = (dates: Date[]) => {
  return dates.map((date) => format(date, "MM-dd-yyyy"));
};

// Get all DateRangeProps from react-date-range to send to the backend as an array of string
export const getAllDatesFromPeriod = (dates: DateRangeProps[]): string[] => {
  const allDates: Date[] = [];

  // Iterate through the list of DateRangeProps and add all the dates between the periods to the allDates array
  for (const dateRange of dates) {
    const { startDate, endDate } = dateRange;
    const datesInRange = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    allDates.push(...datesInRange);
  }

  // Format all the dates in the "MM-dd-yyyy" format and return as an array of strings
  return formatDatesToStrings(allDates);
};

/*  Get the list of weekends and group them where the startDate should be Saturday and the endDate should be Sunday.
      Some months might not have a pair number of consecutive Saturdays and Sundays, which can occur when Saturday is 
     the last day of the month or Sunday is the first day of the month. To handle this, we use the following rule:
     - If we receive Sunday as the startDate, the endDate should also be Saturday.
     - If we receive Saturday as the endDate, the startDate should also be Sunday.
  */
export const groupDatesIntoWeekends = (dates: Date[]): DateRangeProps[] => {
  const weekends = [];
  let startDate = null;

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    // If the day is Friday or Saturday, start a new week
    if (getDay(date) === 5 || getDay(date) === 6) {
      if (!startDate) {
        startDate = date;
      }
      const nextDate = dates[i + 1];
      // Check if the next day belongs to the same week (Saturday or Sunday)
      if (nextDate && (getDay(nextDate) === 6 || getDay(nextDate) === 0)) {
        weekends.push({ startDate, endDate: nextDate });
        startDate = null;
      }
    } else if (getDay(date) === 0 && i === 0) {
      weekends.push({ startDate: date, endDate: date });
    }
  }

  // If there is still a startDate after iterating through all dates and the number of dates is odd, add the last endDate repeating the last startDate
  if (startDate && dates.length % 2 === 1) {
    weekends.push({ startDate, endDate: startDate });
  }

  return weekends;
};

// It return a list of weekenday dates, starting from the today parameter until the last day of the month from the today parameter.
export const getWeekendsRange = (today: Date, latestDate: Date): Date[] => {
  // Calculate all the days of the week between the current date (today) and the last day of the month
  const daysOfWeekInRange = eachDayOfInterval({
    start: today,
    end: latestDate,
  });

  // Filter only the weekend days (Saturday and Sunday)
  const weekDays = daysOfWeekInRange.filter(
    (date) => getDay(date) === 0 || getDay(date) === 5 || getDay(date) === 6
  );

  const parsedDates = weekDays.map((dateString) => new Date(dateString));

  return parsedDates;
};

/*  Get the list of weekdays and group them where the startDate should be Monday and the endDate should be Friday.
      Some months might not have start with Monday or End with a friday, 
     - We check at startOfCurrentWeekFromTheCurrentMonth if monday still the same month if not, we return the first day (date)
  */
export const groupDatesIntoWeeks = (dates: Date[]): DateRangeProps[] => {
  const weeks = [];
  let currentWeek = null as unknown as DateRangeProps;

  for (const date of dates) {
    // Get the start of the week (next Monday)
    const startOfCurrentWeek = startOfWeek(date, { weekStartsOn: 1 }); // 1 represents Monday
    const startOfCurrentWeekFromTheCurrentMonth = isSameMonth(
      startOfCurrentWeek,
      date
    )
      ? startOfCurrentWeek
      : date;

    // If the current date is the start of a new week, create a new week object
    if (
      !currentWeek ||
      !isSameWeek(currentWeek.startDate, startOfCurrentWeekFromTheCurrentMonth)
    ) {
      if (currentWeek) {
        weeks.push(currentWeek);
      }
      currentWeek = {
        startDate: startOfCurrentWeekFromTheCurrentMonth,
        endDate: null as unknown as Date,
      };
    }

    // Update the end of the week to the current date
    currentWeek.endDate = date;
  }

  // Add the last week after iterating through all dates
  if (currentWeek) {
    weeks.push(currentWeek);
  }

  return weeks;
};

// It return a list of weekdays dates, starting from the today parameter until the last day of the month from the today parameter.
export const getWeekdaysRange = (today: Date, latestDate: Date): Date[] => {
  // Calculate all the days of the week between the current date (today) and the last day of the month
  const daysOfWeekInRange = eachDayOfInterval({
    start: today,
    end: latestDate,
  });

  // Filter only the weekdays (Monday to Friday)
  const weekDays = daysOfWeekInRange.filter(
    (date) => getDay(date) >= 1 && getDay(date) <= 4
  );

  const parsedDates = weekDays.map((dateString) => new Date(dateString));

  return parsedDates;
};

export function findEarliestEventDate(events: EventType[]) {
  let earliestDate = new Date();
  try {
    const allDates = events
      .map((event) => convertShortDate(event.date))
      .filter((dt) => {
        const dtText = dt?.toString().toUpperCase();
        return dtText && dtText !== "TBD" && dtText !== "TBA";
      })
      .map((dt) => new Date(dt));
    if (allDates.length) earliestDate = min(allDates);
  } catch (error) {
    console.log(error);
  } finally {
    return earliestDate;
  }
}

export function getBrowserInfo() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const name = browser?.getBrowserName();
  const platform = browser?.getPlatformType();
  const OS = browser.getOS();

  return {
    name,
    platform,
    OS,
  };
}

export function findLatestEventDate(events: EventType[]) {
  let latestDate = new Date();
  try {
    const allDates = events
      .map((event) => convertShortDate(event.date))
      .filter((dt) => {
        const dtText = dt?.toString().toUpperCase();
        return dtText && dtText !== "TBD" && dtText !== "TBA";
      })
      .map((dt) => new Date(dt));
    if (allDates.length) latestDate = max(allDates);
  } catch (error) {
    console.log(error);
  } finally {
    return latestDate;
  }
}
